import axios from 'axios';
import { authErrorCode, bearerToken, isTerminalAuthCode, refreshAccessToken } from './tokenRefresh';

const API_URL = process.env.REACT_APP_API_URL || '';
const AUTH_ENDPOINT_REGEX = /\/api\/auth\/(login|register|refresh)/i;

const isHeaders = (h) => typeof Headers !== 'undefined' && h instanceof Headers;

const getAuthToken = () => {
  try {
    return localStorage.getItem('auth_token');
  } catch {
    return null;
  }
};

const hasAuthHeader = (headers) => {
  if (!headers) return false;
  if (isHeaders(headers)) {
    return headers.has('Authorization') || headers.has('authorization');
  }
  if (Array.isArray(headers)) {
    return headers.some(([key]) => String(key).toLowerCase() === 'authorization');
  }
  if (typeof headers === 'object') {
    if (Object.keys(headers).some((key) => key.toLowerCase() === 'authorization')) {
      return true;
    }
    if (headers.common && typeof headers.common === 'object') {
      return Object.keys(headers.common).some((key) => key.toLowerCase() === 'authorization');
    }
  }
  return false;
};

/** The Authorization header value in any header shape fetch or axios accepts. */
const getAuthHeader = (headers) => {
  if (!headers) return null;
  if (isHeaders(headers)) return headers.get('Authorization');
  if (Array.isArray(headers)) {
    const pair = headers.find(([key]) => String(key).toLowerCase() === 'authorization');
    return pair ? pair[1] : null;
  }
  if (typeof headers === 'object') {
    const key = Object.keys(headers).find((k) => k.toLowerCase() === 'authorization');
    return key ? headers[key] : null;
  }
  return null;
};

/** A copy of `headers` (same shape) carrying `Bearer <token>`. */
const withBearer = (headers, token) => {
  const value = `Bearer ${token}`;
  if (isHeaders(headers)) {
    const next = new Headers(headers);
    next.set('Authorization', value);
    return next;
  }
  if (Array.isArray(headers)) {
    return [...headers.filter(([key]) => String(key).toLowerCase() !== 'authorization'), ['Authorization', value]];
  }
  const next = {};
  Object.entries(headers || {}).forEach(([key, v]) => {
    if (key.toLowerCase() !== 'authorization') next[key] = v;
  });
  next.Authorization = value;
  return next;
};

/**
 * Whether a fetch can be sent a second time unchanged. A Request object or a stream
 * body has been consumed by the first attempt; those 401s sign out as before.
 */
const canReplay = (input, init) => {
  const isUrl = typeof input === 'string' || (typeof URL !== 'undefined' && input instanceof URL);
  if (!isUrl) return false;
  const body = init?.body;
  if (body === undefined || body === null) return true;
  if (typeof body === 'string') return true;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return true;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return true;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return true;
  if (typeof ArrayBuffer !== 'undefined' && (body instanceof ArrayBuffer || ArrayBuffer.isView(body))) return true;
  return false;
};

const isApiUrl = (url) => {
  if (!url) return false;
  if (url.startsWith(API_URL)) return true;
  return url.includes('/api/');
};

const shouldDispatchAuthExpired = (url, headers) => {
  const token = getAuthToken();
  if (!token) return false;
  if (typeof window !== 'undefined' && window.__authExpired) return false;
  if (!isApiUrl(url)) return false;
  if (AUTH_ENDPOINT_REGEX.test(url)) return false;
  if (!hasAuthHeader(headers)) return false;
  return true;
};

/**
 * Two wrappers see every authenticated CRM request that does not go through
 * apiClient: window.fetch (authFetch, apiCache, the AuthContext heartbeat, hand-rolled
 * fetches) and the global axios instance.
 *
 * A 401 from the API used to end the session on the spot. It now first refreshes the
 * access token (tokenRefresh.js — shared with apiClient, so there is only ever one
 * refresh) and replays the request once with the new token. Only when that is not
 * possible does the session end:
 *   - the refresh fails (refresh token expired, revoked, or the account's version moved)
 *   - the 401 carries a terminal code — ACCOUNT_DEACTIVATED, TENANT_MISMATCH,
 *     SESSION_ENDED — which no refresh should paper over
 *   - the replay is 401 too
 *   - the request cannot be replayed (Request object / stream body)
 * Ending the session is unchanged: `auth:expired`, which MainLayout turns into logout.
 */
const installAuthGuards = () => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.__authGuardInstalled) {
    return;
  }

  window.__authGuardInstalled = true;

  const dispatchAuthExpired = (url) => {
    window.__authExpired = true;
    try {
      window.dispatchEvent(new CustomEvent('auth:expired', { detail: { url } }));
    } catch {
      // Ignore event dispatch errors
    }
  };

  if (typeof window.fetch === 'function' && !window.__authFetchWrapped) {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const response = await originalFetch(input, init);
      const requestUrl = typeof input === 'string' ? input : input?.url;
      const requestHeaders = init?.headers || input?.headers;
      if (!(response && response.status === 401 && shouldDispatchAuthExpired(requestUrl || response.url || '', requestHeaders))) {
        return response;
      }

      const code = await response.clone().json().then(authErrorCode, () => null);
      if (!isTerminalAuthCode(code) && canReplay(input, init)) {
        let newToken = null;
        try {
          newToken = await refreshAccessToken(bearerToken(getAuthHeader(requestHeaders)));
        } catch {
          // Refresh failed — the session is over.
        }
        if (newToken) {
          // A network failure on the replay rejects like any fetch; it is not a sign-out.
          const retried = await originalFetch(input, { ...init, headers: withBearer(init?.headers, newToken) });
          if (retried.status === 401) {
            dispatchAuthExpired(retried.url || requestUrl || '');
          }
          return retried;
        }
      }

      dispatchAuthExpired(response.url || requestUrl || '');
      return response;
    };
    window.__authFetchWrapped = true;
  }

  if (!axios.__authInterceptorInstalled) {
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error?.response?.status === 401) {
          const config = error.config || {};
          const requestUrl = config.url || '';
          const requestHeaders = config.headers;
          if (shouldDispatchAuthExpired(requestUrl, requestHeaders)) {
            if (!config.__authRetried && !isTerminalAuthCode(authErrorCode(error.response.data))) {
              let newToken = null;
              try {
                newToken = await refreshAccessToken(bearerToken(getAuthHeader(requestHeaders)));
              } catch {
                // Refresh failed — the session is over.
              }
              if (newToken) {
                config.__authRetried = true;
                if (requestHeaders && typeof requestHeaders.set === 'function') {
                  requestHeaders.set('Authorization', `Bearer ${newToken}`);
                } else {
                  config.headers = withBearer(requestHeaders, newToken);
                }
                return axios(config);
              }
            }
            dispatchAuthExpired(requestUrl);
          }
        }
        return Promise.reject(error);
      }
    );
    axios.__authInterceptorInstalled = true;
  }
};

installAuthGuards();
